import React, { useState, useEffect } from 'react';
import { Card, Typography, Space, Skeleton, Divider } from 'antd';
import { 
    CheckCircleFilled, 
    ExclamationCircleFilled, 
    ClockCircleFilled,
    ReloadOutlined
} from '@ant-design/icons';

const { Text } = Typography;

export default function ClosingChecklist({ situacaoList = [], loading: externalLoading, onRefresh }) {
    const [stats, setStats] = useState({
        lancamentos: { status: 'pending', detail: 'Aguardando dados...', visible: true },
        caixaCredor: { status: 'pending', detail: 'Aguardando dados...', visible: true },
        alertaCaixa: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        faturamento: { status: 'pending', detail: 'Aguardando dados...', visible: true },
        deducoes: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        batimentoSimples: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        folha: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        encargos: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        fornecedores: { status: 'pending', detail: 'Aguardando dados...', visible: true },
        clientes: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        impostos: { status: 'pending', detail: 'Aguardando dados...', visible: true },
        honorarios: { status: 'pending', detail: 'Aguardando dados...', visible: true },
        depreciacao: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        tarifas: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        emprestimos: { status: 'pending', detail: 'Aguardando dados...', visible: false },
        retiradas: { status: 'pending', detail: 'Aguardando dados...', visible: false }
    });

    useEffect(() => {
        if (situacaoList && situacaoList.length > 0) {
            checkAutomaticStatus();
        }
    }, [situacaoList]);

    const checkAutomaticStatus = () => {
        const newStats = { ...stats };

        // --- 1. Lançamentos Mensais (Equilíbrio e Pendências) ---
        const contasRaiz = situacaoList.filter(c => c.nivel === 1);
        const totalDebitos = contasRaiz.reduce((acc, c) => acc + (c.vlTotalDebitos || 0), 0);
        const totalCreditos = contasRaiz.reduce((acc, c) => acc + (c.vlTotalCreditos || 0), 0);
        const equilibrado = Math.abs(totalDebitos - totalCreditos) < 0.05;
        const temPendencias = situacaoList.some(c => 
            ((c.nomeConta || '').toLowerCase().includes('pendente') || (c.nomeConta || '').toLowerCase().includes('identificar')) &&
            Math.abs(c.vlTotalSaldoFinal) > 0.01
        );

        if (!equilibrado) {
            newStats.lancamentos = { status: 'error', detail: 'Balancete desequilibrado.', visible: true };
        } else if (temPendencias) {
            newStats.lancamentos = { status: 'warning', detail: 'Contas pendentes com saldo.', visible: true };
        } else {
            newStats.lancamentos = { status: 'success', detail: 'Lançamentos equilibrados OK.', visible: true };
        }

        // --- 2. Saldo Credor (Caixa e Bancos - 1.1.1) ---
        const temSaldoCredor = situacaoList.some(c => 
            c.codigo.startsWith('1.1.1') && c.vlTotalSaldoFinal < -0.05
        );
        newStats.caixaCredor = temSaldoCredor
            ? { status: 'error', detail: 'Existe saldo credor no Ativo.', visible: true }
            : { status: 'success', detail: 'Saldos do Ativo OK.', visible: true };

        // --- 3. Alerta de Caixa Elevado (> 50k) ---
        const contaCaixa = situacaoList.find(c => c.codigo === '1.1.1.1');
        if (contaCaixa) {
            const saldoCaixa = contaCaixa.vlTotalSaldoFinal || 0;
            newStats.alertaCaixa = {
                status: saldoCaixa > 50000 ? 'warning' : 'success',
                detail: saldoCaixa > 50000 ? `Saldo alto: R$ ${saldoCaixa.toLocaleString('pt-BR')}` : 'Saldo sob controle.',
                visible: true
            };
        } else {
            newStats.alertaCaixa = { status: 'success', detail: 'N/A', visible: false };
        }

        // --- 4. Faturamento (Receita Bruta - 4.1.1) ---
        const temReceita = situacaoList.some(c =>
            c.codigo.startsWith('4.1.1') &&
            (Math.abs(c.vlTotalCreditos) > 0.01 || Math.abs(c.vlTotalSaldoFinal) > 0.01)
        );
        newStats.faturamento = temReceita
            ? { status: 'success', detail: 'Faturamento identificado.', visible: true }
            : { status: 'error', detail: 'Sem receita no período.', visible: true };

        // --- 5. Deduções da Receita (4.1.3) ---
        if (temReceita) {
            const temDeducoes = situacaoList.some(c => 
                c.codigo.startsWith('4.1.3') && 
                (Math.abs(c.vlTotalDebitos) > 0.01 || Math.abs(c.vlTotalSaldoFinal) > 0.01)
            );
            newStats.deducoes = { 
                status: temDeducoes ? 'success' : 'warning', 
                detail: temDeducoes ? 'Impostos s/ vendas OK.' : 'Verificar deduções (Simples/ISS).', 
                visible: true 
            };

            // --- 6. Batimento Simples Nacional (4.1.3.1 vs 2.1.3.1) ---
            const simplesDRE = situacaoList.find(c => c.codigo === '4.1.3.1');
            const simplesPassivo = situacaoList.find(c => c.codigo === '2.1.3.1');
            if (simplesDRE || simplesPassivo) {
                const valDRE = Math.abs(simplesDRE?.vlTotalDebitos || 0);
                const valPassivo = Math.abs(simplesPassivo?.vlTotalCreditos || 0);
                const bateu = Math.abs(valDRE - valPassivo) < 1.00; // Tolerância de R$ 1 por arredondamento
                newStats.batimentoSimples = {
                    status: bateu ? 'success' : 'error',
                    detail: bateu ? 'DRE vs Passivo bateu.' : 'Diferença no Simples Nac.',
                    visible: true
                };
            } else {
                newStats.batimentoSimples = { status: 'success', detail: 'N/A', visible: false };
            }
        } else {
            newStats.deducoes = { status: 'success', detail: 'N/A', visible: false };
            newStats.batimentoSimples = { status: 'success', detail: 'N/A', visible: false };
        }

        // --- 7. Folha e Pró-labore ---
        const temContextoFolha = situacaoList.some(c =>
            (c.codigo.startsWith('2.1.4') || c.codigo.startsWith('5.2.1')) &&
            (Math.abs(c.vlTotalCreditos) > 0.01 || Math.abs(c.vlTotalDebitos) > 0.01 || Math.abs(c.vlTotalSaldoAnterior) > 0.01)
        );

        if (temContextoFolha) {
            const temMovFolha = situacaoList.some(c =>
                c.codigo.startsWith('2.1.4') && (Math.abs(c.vlTotalCreditos) > 0.01 || Math.abs(c.vlTotalDebitos) > 0.01)
            );
            newStats.folha = { 
                status: temMovFolha ? 'success' : 'warning', 
                detail: temMovFolha ? 'Provisões identificadas.' : 'Falta provisão de folha.', 
                visible: true 
            };

            // --- 8. Encargos (INSS/FGTS) ---
            const temDespesaSalario = situacaoList.some(c => 
                (c.codigo === '5.2.1.1' || c.codigo === '5.2.1.2') && Math.abs(c.vlTotalDebitos) > 0.01
            );
            if (temDespesaSalario) {
                const temEncargos = situacaoList.some(c => 
                    (c.codigo === '5.2.1.6' || c.codigo === '5.2.1.7') && Math.abs(c.vlTotalDebitos) > 0.01
                );
                newStats.encargos = { 
                    status: temEncargos ? 'success' : 'warning', 
                    detail: temEncargos ? 'Encargos identificados.' : 'Verificar INSS/FGTS.', 
                    visible: true 
                };
            } else {
                newStats.encargos = { status: 'success', detail: 'N/A', visible: false };
            }
        } else {
            newStats.folha = { status: 'success', detail: 'N/A', visible: false };
            newStats.encargos = { status: 'success', detail: 'N/A', visible: false };
        }

        // --- 9. Fornecedores ---
        const temMovFornecedores = situacaoList.some(c =>
            c.codigo.startsWith('2.1.1.1') && (Math.abs(c.vlTotalCreditos) > 0.01 || Math.abs(c.vlTotalDebitos) > 0.01)
        );
        newStats.fornecedores = { 
            status: temMovFornecedores ? 'success' : 'warning', 
            detail: temMovFornecedores ? 'Notas de fornecedores OK.' : 'Sem notas de fornecedores.', 
            visible: true 
        };

        // --- 10. Clientes ---
        const temSaldoClientes = situacaoList.some(c => 
            c.codigo.startsWith('1.1.3.1') && Math.abs(c.vlTotalSaldoFinal) > 0.01
        );
        if (temSaldoClientes) {
            const temRecebimentos = situacaoList.some(c => 
                c.codigo.startsWith('1.1.3.1') && Math.abs(c.vlTotalCreditos) > 0.01
            );
            newStats.clientes = { 
                status: temRecebimentos ? 'success' : 'warning', 
                detail: temRecebimentos ? 'Recebimentos identificados.' : 'Verificar baixas de clientes.', 
                visible: true 
            };
        } else {
            newStats.clientes = { status: 'success', detail: 'N/A', visible: false };
        }

        // --- 11. Impostos (2.1.3) ---
        const temMovImpostos = situacaoList.some(c =>
            c.codigo.startsWith('2.1.3') && (Math.abs(c.vlTotalCreditos) > 0.01 || Math.abs(c.vlTotalDebitos) > 0.01)
        );
        newStats.impostos = { 
            status: temMovImpostos ? 'success' : 'warning', 
            detail: temMovImpostos ? 'Impostos identificados.' : 'Sem provisão de impostos.', 
            visible: true 
        };

        // --- 12. Honorários Contábeis ---
        const temHonorarios = situacaoList.some(c => 
            c.codigo === '5.2.2.11' && Math.abs(c.vlTotalDebitos) > 0.01
        );
        newStats.honorarios = { 
            status: temHonorarios ? 'success' : 'warning', 
            detail: temHonorarios ? 'Honorários identificados.' : 'Falta lançar honorários.', 
            visible: true 
        };

        // --- 13. Depreciação ---
        const temImobilizado = situacaoList.some(c => 
            c.codigo.startsWith('1.2.2') && Math.abs(c.vlTotalSaldoFinal) > 0.01
        );
        if (temImobilizado) {
            const temDepreciacao = situacaoList.some(c => 
                (c.codigo.startsWith('1.2.3') || c.codigo.startsWith('5.2.2.33')) && 
                (Math.abs(c.vlTotalCreditos) > 0.01 || Math.abs(c.vlTotalDebitos) > 0.01)
            );
            newStats.depreciacao = { 
                status: temDepreciacao ? 'success' : 'warning', 
                detail: temDepreciacao ? 'Depreciação lançada.' : 'Falta lançar depreciação.', 
                visible: true 
            };
        } else {
            newStats.depreciacao = { status: 'success', detail: 'N/A', visible: false };
        }

        // --- 14. Tarifas Bancárias ---
        const temMovBanco = situacaoList.some(c => 
            c.codigo.startsWith('1.1.1') && c.codigo !== '1.1.1.1' && 
            (Math.abs(c.vlTotalCreditos) > 0.01 || Math.abs(c.vlTotalDebitos) > 0.01)
        );
        if (temMovBanco) {
            const temTarifas = situacaoList.some(c => 
                c.codigo.startsWith('5.2.3.6') && Math.abs(c.vlTotalDebitos) > 0.01
            );
            newStats.tarifas = { 
                status: temTarifas ? 'success' : 'warning', 
                detail: temTarifas ? 'Tarifas identificadas.' : 'Verificar tarifas bancárias.', 
                visible: true 
            };
        } else {
            newStats.tarifas = { status: 'success', detail: 'N/A', visible: false };
        }

        // --- 15. Empréstimos ---
        const temSaldoEmprestimo = situacaoList.some(c => 
            c.codigo.startsWith('2.1.2') && Math.abs(c.vlTotalSaldoFinal) > 0.01
        );
        if (temSaldoEmprestimo) {
            const temAmortizacao = situacaoList.some(c => 
                (c.codigo.startsWith('2.1.2') || c.codigo.startsWith('5.2.3.4')) && 
                (Math.abs(c.vlTotalDebitos) > 0.01 || Math.abs(c.vlTotalCreditos) > 0.01)
            );
            newStats.emprestimos = { 
                status: temAmortizacao ? 'success' : 'warning', 
                detail: temAmortizacao ? 'Movimento de empréstimo OK.' : 'Verificar juros/amortização.', 
                visible: true 
            };
        } else {
            newStats.emprestimos = { status: 'success', detail: 'N/A', visible: false };
        }

        // --- 16. Retiradas de Sócios ---
        const temRetirada = situacaoList.some(c => 
            (c.codigo === '1.1.4.5' || c.codigo === '2.3.3.6') && 
            (Math.abs(c.vlTotalDebitos) > 0.01 || Math.abs(c.vlTotalSaldoFinal) > 0.01)
        );
        if (temRetirada) {
            newStats.retiradas = { 
                status: 'warning', 
                detail: 'Conferir limite de lucros.', 
                visible: true 
            };
        } else {
            newStats.retiradas = { status: 'success', detail: 'N/A', visible: false };
        }

        setStats(newStats);
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'success': return <CheckCircleFilled style={{ color: '#52c41a', fontSize: '16px' }} />;
            case 'warning': return <ExclamationCircleFilled style={{ color: '#faad14', fontSize: '16px' }} />;
            case 'error': return <ExclamationCircleFilled style={{ color: '#ff4d4f', fontSize: '16px' }} />;
            default: return <ClockCircleFilled style={{ color: '#bfbfbf', fontSize: '16px' }} />;
        }
    };

    const allSuccess = Object.values(stats)
        .filter(s => s.visible !== false)
        .every(s => s.status === 'success');

    const visibleItems = Object.entries(stats).filter(([_, s]) => s.visible);

    if (externalLoading) {
        return (
            <Card
                title={
                    <Space>
                        <CheckCircleFilled style={{ color: '#4f46e5' }} />
                        <Text strong style={{ fontSize: '14px' }}>Auditoria de Fechamento</Text>
                    </Space>
                }
                style={{ 
                    width: '100%', 
                    borderRadius: '12px',
                    border: '1px solid #e2e8f0',
                    boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
                }}
                bodyStyle={{ padding: '24px' }}
            >
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: '20px' }}>
                    {Array.from({ length: 8 }).map((_, i) => (
                        <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                            <Skeleton.Avatar active size="small" shape="circle" />
                            <div style={{ flex: 1 }}>
                                <Skeleton.Button active style={{ width: '80%', height: '14px', marginBottom: '4px' }} />
                                <Skeleton.Button active style={{ width: '60%', height: '10px' }} />
                            </div>
                        </div>
                    ))}
                </div>
            </Card>
        );
    }

    return (
        <Card
            title={
                <Space>
                    <CheckCircleFilled style={{ color: '#4f46e5' }} />
                    <Text strong style={{ fontSize: '14px' }}>Auditoria de Fechamento</Text>
                </Space>
            }
            extra={
                onRefresh && (
                    <ReloadOutlined 
                        style={{ cursor: 'pointer', color: '#64748b' }} 
                        onClick={onRefresh}
                        spin={externalLoading}
                    />
                )
            }
            style={{ 
                width: '100%', 
                borderRadius: '12px',
                border: '1px solid #e2e8f0',
                boxShadow: '0 1px 2px 0 rgba(0, 0, 0, 0.05)'
            }}
            bodyStyle={{ padding: '16px' }}
        >
            <div style={{ 
                display: 'grid', 
                gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', 
                gap: '16px 12px' 
            }}>
                {visibleItems.map(([key, item]) => (
                    <div key={key} style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        {getStatusIcon(item.status)}
                        <div>
                            <Text strong style={{ fontSize: '11px', display: 'block', lineHeight: '1.2' }}>
                                {getLabel(key)}
                            </Text>
                            <Text type="secondary" style={{ fontSize: '9px', display: 'block' }}>
                                {item.detail}
                            </Text>
                        </div>
                    </div>
                ))}
            </div>

            <Divider style={{ margin: '12px 0' }} />

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Text style={{ fontSize: '11px', color: '#64748b' }}>
                    {allSuccess ? 'Tudo pronto para fechar!' : 'Ainda restam pendências.'}
                </Text>
                <div style={{
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    backgroundColor: allSuccess ? '#f6ffed' : '#fff7e6',
                    color: allSuccess ? '#52c41a' : '#faad14',
                    fontWeight: 600
                }}>
                    {allSuccess ? 'LIBERADO' : 'PENDENTE'}
                </div>
            </div>
        </Card>
    );
}

function getLabel(key) {
    const labels = {
        lancamentos: 'Equilíbrio Contábil',
        caixaCredor: 'Saldos do Ativo',
        alertaCaixa: 'Caixa Elevado',
        faturamento: 'Faturamento',
        deducoes: 'Impostos s/ Vendas',
        batimentoSimples: 'Simples: DRE vs Passivo',
        folha: 'Folha Pagto',
        encargos: 'Encargos (INSS/FGTS)',
        fornecedores: 'Fornecedores',
        clientes: 'Recebimentos (Clientes)',
        impostos: 'Impostos',
        honorarios: 'Honorários Contábeis',
        depreciacao: 'Depreciação',
        tarifas: 'Tarifas Bancárias',
        emprestimos: 'Empréstimos/Juros',
        retiradas: 'Retirada de Sócios'
    };
    return labels[key] || key;
}
